import { cn } from '@undp/design-system-react/cn';
import { P } from '@undp/design-system-react/Typography';
import { geoPath } from 'd3-geo';
import { scaleThreshold } from 'd3-scale';
import isEqual from 'fast-deep-equal';
import type { FeatureCollection } from 'geojson';
import { AnimatePresence, motion, useInView } from 'motion/react';
import { type RefObject, useMemo, useRef, useState } from 'react';
import type { Topology } from 'topojson-specification';
import { CsvDownloadButton } from '@/Components/Actions/CsvDownloadButton';
import { ImageDownloadButton } from '@/Components/Actions/ImageDownloadButton';
import { DetailsModal } from '@/Components/Elements/DetailsModal';
import {
  LegendCollapseButton,
  LegendExpandButton,
} from '@/Components/Elements/LegendExpandControls';
import { MapZoomButton } from '@/Components/Elements/MapZoomButton';
import { Tooltip } from '@/Components/Elements/Tooltip';
import { useMapProjection } from '@/hooks/useMapProjection';
import { useMapZoom } from '@/hooks/useMapZoom';
import type {
  AnimateDataType,
  BivariateMapDataType,
  ClassNameObject,
  CustomLayerDataType,
  MapProjectionTypes,
  NumberFormatOptions,
  StyleObject,
  ZoomInteractionTypes,
} from '@/Types';
import { convertTopoJsonToGeoJson } from '@/Utils';
import { checkIfNullOrUndefined } from '@/Utils/checkIfNullOrUndefined';
import { numberFormattingFunction } from '@/Utils/numberFormattingFunction';
import AksaiChinStriped from '../shapeFile/aksaiChinStriped.json';

interface Props {
  data: BivariateMapDataType[];
  mapData: FeatureCollection;
  mapBorderData?: FeatureCollection;
  xDomain: number[];
  yDomain: number[];
  width: number;
  height: number;
  colors: string[][];
  xColorLegendTitle: string;
  yColorLegendTitle: string;
  mapBorderWidth: number;
  mapNoDataColor: string;
  scale: number;
  centerPoint?: [number, number];
  mapBorderColor: string;
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  tooltip?: string | ((_d: any) => React.ReactNode);
  zoomInteraction: ZoomInteractionTypes;
  mapProjection: MapProjectionTypes;

  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  onSeriesMouseOver?: (_d: any) => void;
  zoomScaleExtend: [number, number];
  zoomTranslateExtend?: [[number, number], [number, number]];
  highlightedIds?: string[];

  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  onSeriesMouseClick?: (_d: any) => void;
  mapProperty: string;
  resetSelectionOnDoubleClick: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  detailsOnClick?: string | ((_d: any) => React.ReactNode);
  showColorScale: boolean;
  collapseColorScaleByDefault?: boolean;
  styles?: StyleObject;
  classNames?: ClassNameObject;
  animate: AnimateDataType;
  dimmedOpacity: number;
  customLayers: CustomLayerDataType[];
  zoomAndCenterByHighlightedIds: boolean;
  projectionRotate: [number, number] | [number, number, number];
  overlayMapData?: FeatureCollection;
  overlayMapBorderColor?: string;
  overlayMapBorderWidth?: number;
  xNumberDisplayOptions?: Omit<NumberFormatOptions, 'suffix' | 'prefix'>;
  yNumberDisplayOptions?: Omit<NumberFormatOptions, 'suffix' | 'prefix'>;
  graphDownload?: RefObject<HTMLDivElement | null>;
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  dataDownload: any;
  showUNBorder: boolean;
  showAksaiChinAsStriped: boolean;
  isDisputedAreasInteractive: boolean;
}

export function Graph(props: Props) {
  const {
    data,
    xDomain,
    mapData,
    xColorLegendTitle,
    yDomain,
    yColorLegendTitle,
    width,
    height,
    colors,
    scale,
    centerPoint,
    mapBorderWidth,
    mapNoDataColor,
    mapBorderColor,
    tooltip,
    onSeriesMouseOver,
    zoomScaleExtend,
    zoomTranslateExtend,
    highlightedIds,
    onSeriesMouseClick,
    mapProperty,
    resetSelectionOnDoubleClick,
    detailsOnClick,
    showColorScale,
    styles,
    classNames,
    mapProjection,
    zoomInteraction,
    animate,
    dimmedOpacity,
    customLayers,
    collapseColorScaleByDefault,
    zoomAndCenterByHighlightedIds,
    projectionRotate,
    overlayMapData,
    overlayMapBorderColor,
    overlayMapBorderWidth,
    xNumberDisplayOptions,
    yNumberDisplayOptions,
    graphDownload,
    dataDownload,
    mapBorderData,
    showUNBorder,
    showAksaiChinAsStriped,
    isDisputedAreasInteractive,
  } = props;

  const aksaiChinStripedGeoJson = useMemo(
    () => convertTopoJsonToGeoJson(AksaiChinStriped as unknown as Topology, 'stripes'),
    [],
  );
  const [showLegend, setShowLegend] = useState(
    collapseColorScaleByDefault === undefined ? !(width < 680) : !collapseColorScaleByDefault,
  );
  const [selectedColor, setSelectedColor] = useState<string | undefined>(undefined);
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  const [mouseClickData, setMouseClickData] = useState<any>(undefined);
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  const [mouseOverData, setMouseOverData] = useState<any>(undefined);
  const [eventX, setEventX] = useState<number | undefined>(undefined);
  const [eventY, setEventY] = useState<number | undefined>(undefined);
  const mapSvg = useRef<SVGSVGElement>(null);
  const mapG = useRef<SVGGElement>(null);

  const { handleZoom } = useMapZoom({
    mapSvg,
    mapG,
    width,
    height,
    zoomInteraction,
    zoomScaleExtend,
    zoomTranslateExtend,
  });
  const { projection } = useMapProjection({
    mapData,
    mapProperty,
    zoomAndCenterByHighlightedIds,
    highlightedIds: highlightedIds || [],
    width,
    height,
    mapProjection,
    scale,
    centerPoint,
    projectionRotate,
  });
  const isInView = useInView(mapSvg, {
    once: animate.once,
    amount: animate.amount,
  });

  const pathGenerator = geoPath().projection(projection);
  const xRange = Array.from({ length: xDomain.length }, (_, i) => i);

  const yRange = Array.from({ length: yDomain.length }, (_, i) => i);
  const xScale = scaleThreshold<number, number>().domain(xDomain).range(xRange);
  const yScale = scaleThreshold<number, number>().domain(yDomain).range(yRange);

  return (
    <>
      <div className='relative'>
        <motion.svg
          viewBox={`0 0 ${width} ${height}`}
          width={`${width}px`}
          height={`${height}px`}
          ref={mapSvg}
          direction='ltr'
        >
          <g ref={mapG}>
            {customLayers.filter((d) => d.position === 'before').map((d) => d.layer)}
            {mapData.features.map((d, i: number) => {
              if (!d.properties?.[mapProperty]) return null;
              const path = pathGenerator(d);
              if (!path) return null;
              return (
                <motion.g
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                  key={i}
                  opacity={
                    selectedColor
                      ? dimmedOpacity
                      : highlightedIds
                        ? highlightedIds.indexOf(d.properties[mapProperty]) !== -1
                          ? d.properties?.iso3cd?.[0] !== 'x' || isDisputedAreasInteractive
                            ? 1
                            : dimmedOpacity
                          : dimmedOpacity
                        : 1
                  }
                >
                  <path
                    d={path}
                    style={{
                      fill: mapNoDataColor,
                      vectorEffect: 'non-scaling-stroke',
                    }}
                  />
                </motion.g>
              );
            })}
            <AnimatePresence>
              {data.map((d) => {
                const features = mapData.features.filter(
                  (el) => d.id === el.properties?.[mapProperty],
                );
                if (features.length === 0) return null;
                const xColorCoord = !checkIfNullOrUndefined(d.x)
                  ? xScale(d.x as number)
                  : undefined;
                const yColorCoord = !checkIfNullOrUndefined(d.y)
                  ? yScale(d.y as number)
                  : undefined;
                const color =
                  xColorCoord !== undefined && yColorCoord !== undefined
                    ? colors[yColorCoord][xColorCoord]
                    : mapNoDataColor;

                return (
                  <motion.g
                    className='undp-map-shapes'
                    key={d.id}
                    variants={{
                      initial: { opacity: 0 },
                      whileInView: {
                        opacity: 1,
                        transition: { duration: animate.duration },
                      },
                    }}
                    initial='initial'
                    animate={isInView ? 'whileInView' : 'initial'}
                    exit={{ opacity: 0, transition: { duration: animate.duration } }}
                    onMouseEnter={(event) => {
                      setMouseOverData(d);
                      setEventY(event.clientY);
                      setEventX(event.clientX);
                      onSeriesMouseOver?.(d);
                    }}
                    onMouseMove={(event) => {
                      setMouseOverData(d);
                      setEventY(event.clientY);
                      setEventX(event.clientX);
                    }}
                    onClick={() => {
                      if (onSeriesMouseClick || detailsOnClick) {
                        if (isEqual(mouseClickData, d) && resetSelectionOnDoubleClick) {
                          setMouseClickData(undefined);
                          onSeriesMouseClick?.(undefined);
                        } else {
                          setMouseClickData(d);
                          onSeriesMouseClick?.(d);
                        }
                      }
                    }}
                    onMouseLeave={() => {
                      setMouseOverData(undefined);
                      setEventX(undefined);
                      setEventY(undefined);
                      onSeriesMouseOver?.(undefined);
                    }}
                  >
                    {features.map((feature, i) => {
                      const path = pathGenerator(feature);
                      if (!path) return null;
                      return (
                        <motion.path
                          key={`${d.id}-${
                            // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                            i
                          }`}
                          d={path}
                          variants={{
                            initial: { fill: color, opacity: 0 },
                            whileInView: {
                              fill: color,
                              opacity: selectedColor
                                ? selectedColor === color
                                  ? 1
                                  : dimmedOpacity
                                : mouseOverData
                                  ? mouseOverData.id === d.id
                                    ? 1
                                    : dimmedOpacity
                                  : highlightedIds
                                    ? highlightedIds.indexOf(d.id) !== -1
                                      ? feature.properties?.iso3cd?.[0] !== 'x' ||
                                        isDisputedAreasInteractive
                                        ? 1
                                        : dimmedOpacity
                                      : dimmedOpacity
                                    : 1,
                              pointerEvents:
                                feature.properties?.iso3cd?.[0] === 'x' &&
                                !isDisputedAreasInteractive
                                  ? 'none'
                                  : 'auto',
                              transition: { duration: animate.duration },
                            },
                          }}
                          initial='initial'
                          animate={isInView ? 'whileInView' : 'initial'}
                          exit={{ opacity: 0, transition: { duration: animate.duration } }}
                        />
                      );
                    })}
                  </motion.g>
                );
              })}
              {showAksaiChinAsStriped &&
                aksaiChinStripedGeoJson?.features.map((d, i: number) => {
                  const path = pathGenerator(d);
                  const dataForCHN = data.find((el) => el.id === 'CHN');
                  const xColorCoord = !checkIfNullOrUndefined(dataForCHN?.x)
                    ? xScale(dataForCHN?.x as number)
                    : undefined;
                  const yColorCoord = !checkIfNullOrUndefined(dataForCHN?.y)
                    ? yScale(dataForCHN?.y as number)
                    : undefined;
                  const color =
                    xColorCoord !== undefined && yColorCoord !== undefined
                      ? colors[yColorCoord][xColorCoord]
                      : mapNoDataColor;
                  if (!path) return null;
                  return (
                    <motion.g
                      // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                      key={`aksai-chin-stripped-${i}`}
                      variants={{
                        initial: { opacity: 0 },
                        whileInView: {
                          opacity: selectedColor || highlightedIds?.length ? dimmedOpacity : 1,
                          transition: { duration: animate.duration },
                        },
                      }}
                      initial='initial'
                      animate={isInView ? 'whileInView' : 'initial'}
                      exit={{ opacity: 0, transition: { duration: animate.duration } }}
                    >
                      <motion.path
                        d={path}
                        // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                        key={`aksai-chin-stripped-${i}`}
                        variants={{
                          initial: { fill: color, opacity: 0 },
                          whileInView: {
                            fill: color,
                            opacity: 1,
                            transition: { duration: animate.duration },
                          },
                        }}
                        initial='initial'
                        animate={isInView ? 'whileInView' : 'initial'}
                        exit={{ opacity: 0, transition: { duration: animate.duration } }}
                      />
                    </motion.g>
                  );
                })}
            </AnimatePresence>
            {showUNBorder &&
              (mapBorderData || mapData)?.features.map((d, i: number) => {
                if (!d.properties?.[mapBorderData ? 'iso3cd' : mapProperty]) return null;
                const path = pathGenerator(d);
                if (!path || d.properties?.bdytyp === 99) return null;
                return (
                  <motion.g
                    // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                    key={i}
                  >
                    <path
                      d={path}
                      style={{
                        stroke: mapBorderColor,
                        strokeWidth:
                          d.properties?.bdytyp === 3 || d.properties?.bdytyp === 4
                            ? Math.max(1, mapBorderWidth)
                            : mapBorderWidth,
                        fill: 'none',
                        vectorEffect: 'non-scaling-stroke',
                        strokeDasharray:
                          d.properties?.bdytyp === 3
                            ? '3 3'
                            : d.properties?.bdytyp === 4
                              ? '2 2'
                              : undefined,
                      }}
                    />
                  </motion.g>
                );
              })}
            {overlayMapData?.features.map((d, i: number) => {
              const path = pathGenerator(d);
              if (!path) return null;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                <g key={i}>
                  <path
                    d={path}
                    style={{
                      stroke: overlayMapBorderColor || mapBorderColor,
                      strokeWidth: overlayMapBorderWidth || mapBorderWidth + 1,
                      fill: 'none',
                      pointerEvents: 'none',
                      vectorEffect: 'non-scaling-stroke',
                    }}
                  />
                </g>
              );
            })}
            {customLayers.filter((d) => d.position === 'after').map((d) => d.layer)}
          </g>
        </motion.svg>
        {showColorScale === false ? null : (
          <div className={cn('absolute left-4 bottom-4 map-color-legend', classNames?.colorLegend)}>
            {showLegend ? (
              <>
                <LegendCollapseButton setExpanded={setShowLegend} />
                <div className='color-legend-box p-2 bg-surface-sm/70' style={{ width: '175px' }}>
                  <div className='flex gap-1 items-center'>
                    <svg width='136px' viewBox='0 0 136 136' className='shrink-0'>
                      <g>
                        {colors.map((d, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                          <g key={i} transform={`translate(0,${100 - i * 25})`}>
                            {d.map((el, j) => (
                              // biome-ignore lint/a11y/noStaticElementInteractions: interaction for color legend
                              <rect
                                // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                                key={j}
                                y={1}
                                x={j * 25 + 1}
                                fill={el}
                                width={23}
                                height={23}
                                strokeWidth={selectedColor === el ? 2 : 0.25}
                                style={{ cursor: 'pointer' }}
                                onMouseOver={() => {
                                  setSelectedColor(el);
                                }}
                                onFocus={() => {
                                  setSelectedColor(el);
                                }}
                                onMouseLeave={() => {
                                  setSelectedColor(undefined);
                                }}
                              />
                            ))}
                          </g>
                        ))}
                        <g transform='translate(0,125)'>
                          {xDomain.map((el, j) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                            <text key={j} y={10} x={(j + 1) * 25} fontSize={10} textAnchor='middle'>
                              {typeof el === 'string' || el < 1
                                ? el
                                : numberFormattingFunction(
                                    el,
                                    undefined,
                                    xNumberDisplayOptions?.precision ?? 2,
                                    undefined,
                                    undefined,
                                    xNumberDisplayOptions?.locale || 'en',
                                    xNumberDisplayOptions?.padZeros || 'none',
                                  )}
                            </text>
                          ))}
                        </g>
                        {yDomain.map((el, j) => (
                          <g
                            // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                            key={j}
                            transform={`translate(${
                              Math.max(Math.min(xDomain.length + 1, 5), 4) * 25 + 10
                            },${100 - j * 25})`}
                          >
                            <text
                              x={0}
                              transform='rotate(-90)'
                              y={0}
                              fontSize={10}
                              textAnchor='middle'
                            >
                              {typeof el === 'string' || el < 1
                                ? el
                                : numberFormattingFunction(
                                    el,
                                    undefined,
                                    yNumberDisplayOptions?.precision ?? 2,
                                    undefined,
                                    undefined,
                                    yNumberDisplayOptions?.locale || 'en',
                                    yNumberDisplayOptions?.padZeros || 'none',
                                  )}
                            </text>
                          </g>
                        ))}
                      </g>
                    </svg>
                    <P
                      marginBottom='none'
                      size='xs'
                      className='leading-normal text-center shrink-0'
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        writingMode: 'vertical-rl',
                        height: '8.5rem',
                        transform: 'rotate(180deg)',
                      }}
                    >
                      {yColorLegendTitle}
                    </P>
                  </div>
                  <P
                    marginBottom='none'
                    size='xs'
                    className='mt-1 leading-normal text-center'
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: '1',
                      width: '8.5rem',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {xColorLegendTitle}
                  </P>
                </div>
              </>
            ) : (
              <LegendExpandButton setExpanded={setShowLegend} />
            )}
          </div>
        )}
        {zoomInteraction === 'button' && <MapZoomButton handleZoom={handleZoom} />}
        {(graphDownload || dataDownload) && (
          <div className='absolute right-4 top-4 flex flex-col image-download-button gap-2'>
            {graphDownload && (
              <ImageDownloadButton nodeID={graphDownload} buttonSmall className='p-1' />
            )}
            {dataDownload && dataDownload.length > 0 && (
              <CsvDownloadButton
                csvData={dataDownload}
                buttonSmall
                headers={Object.keys(dataDownload[0]).map((d) => ({
                  label: d,
                  key: d,
                }))}
                className='p-1'
              />
            )}
          </div>
        )}
      </div>
      {mouseOverData && tooltip && eventX && eventY ? (
        <Tooltip
          data={mouseOverData}
          body={tooltip}
          xPos={eventX}
          yPos={eventY}
          backgroundStyle={styles?.tooltip}
          className={classNames?.tooltip}
        />
      ) : null}
      {detailsOnClick && mouseClickData !== undefined ? (
        <DetailsModal
          body={detailsOnClick}
          data={mouseClickData}
          setData={setMouseClickData}
          className={classNames?.modal}
        />
      ) : null}
    </>
  );
}
